import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {ArrowRight, FileText} from '../../../icons';
import {boosterFlowPath} from '../boosterSteps';
import {useSourcesDrawer} from './sourcesDrawerContext';

// El ancla con la que el Recorrido entiende "abrí el material": es lo que ve un link a esta línea
// desde afuera del panel (Ctrl/Cmd+clic, copiar enlace) y lo que el contenedor traduce en abrir el
// panel de Fuentes al aterrizar.
export const SOURCES_ANCHOR = 'fuentes';

// Cuánto queda en pantalla la confirmación de que algo se guardó, antes de volver al conteo. Es el
// número del mockup: alcanza para leer una línea y no deja a la línea diciendo "recién" a las
// tres de la tarde.
export const FRESH_MS = 5000;

/**
 * Singular y plural explícitos, sin `count`.
 *
 * `count` es la variable con la que i18next dispara su pluralización y saldría a buscar claves
 * `_one`/`_other` que este repo no escribe. Elegir la clave acá es una línea y no deja el número
 * dependiendo de una convención invisible.
 */
const unitLabel = (t, n, oneKey, manyKey) => (
    n === 1 ? t(oneKey) : t(manyKey, {n})
);

// Los dos huecos que se interpolan en la frase para poder partirla después y resaltar los conteos.
// Van como texto plano y no como un carácter invisible: se leen en el código, no se pueden colar en
// un copy por accidente y no dejan bytes raros en el archivo.
const DOCS_SLOT = '[[docs]]';
const NOTES_SLOT = '[[notes]]';

/**
 * «**18 documentos** y **15 notas** alimentan esta memoria» — con los conteos en blanco puro.
 *
 * El mockup los pone en `<b>` (`.sl-txt b{color:#fff}`) y saltan contra el resto de la frase, que va
 * en el crema del lienzo. La regla CSS ya estaba escrita en el SCSS del frente y era **CSS muerto**:
 * el componente nunca emitía el `<b>`.
 *
 * Se compone partiendo la frase YA TRADUCIDA, en vez de meter markup adentro de la clave: así el
 * orden de las dos piezas lo sigue decidiendo el idioma —en inglés la frase no tiene por qué
 * ordenarlas igual— y no hay HTML en el i18n. Si una traducción se comiera un `{{…}}`, ese conteo
 * simplemente no sale en negrita y la frase se lee igual: no hay forma de que quede partida.
 */
const withBoldCounts = (line, docs, notes) => {
    // El grupo de captura hace que `split` devuelva TAMBIÉN los separadores, así que la frase queda
    // como [texto, hueco, texto, hueco, texto] y se puede recomponer sin asumir en qué orden los
    // nombra el idioma.
    const partes = line.split(/(\[\[docs\]\]|\[\[notes\]\])/);
    return partes.map((parte, i) => {
        if (parte === DOCS_SLOT) return <b key={i}>{docs}</b>;
        if (parte === NOTES_SLOT) return <b key={i}>{notes}</b>;
        return <React.Fragment key={i}>{parte}</React.Fragment>;
    });
};

/**
 * «N documentos y M notas alimentan esta memoria».
 *
 * Es la línea que cierra el acto Alimenta: dice de qué está hecha la memoria y da la puerta para
 * gestionarla. Los dos números vienen de arriba (`FeedActContent`) porque salen de los mismos dos
 * listados que ya se piden para el resto del acto — pedirlos de nuevo acá sería una tercera consulta
 * a endpoints que ya están en pantalla.
 *
 * **«Ver y gestionar» abre el panel de Fuentes**, no scrollea a una tabla. La tabla ya no vive
 * inline en el acto (mockup: `openDocs()`); vive en `SourcesDrawer`, y esta línea es su puerta
 * principal. Sin provider (fuera del Recorrido) cae a un link al ancla `#fuentes`, que el Recorrido
 * traduce en abrir el panel.
 *
 * **La confirmación es una línea, no un cartel huérfano** (mockup: `srcConfirm`). Cuando un
 * documento de esta visita termina de extraerse, la línea dice durante unos segundos que se guardó
 * y cuántos hechos encontró, y vuelve sola al conteo. Se entiende que quedó guardado y dónde
 * encontrarlo, sin un toast que tape lo que viene abajo.
 *
 * Las partículas son CSS: puntos que suben con un `animation-delay` distinto cada uno. No hay
 * `<canvas>` nuevo — el único de esta pantalla es el del orbe.
 */
export function SourceLine({
    clientId,
    documentCount = null,
    noteCount = null,
    loading = false,
    // El último documento de esta visita que terminó de extraerse: `{id, title, facts}`. Con
    // `facts` en número la línea dice cuántos encontró; sin él, sólo que se guardó.
    fresh = null,
}) {
    const {t} = useTranslation();
    const drawer = useSourcesDrawer();
    const known = typeof documentCount === 'number' && typeof noteCount === 'number';
    const [showingFresh, setShowingFresh] = useState(false);

    // La confirmación se identifica por `fresh.id`, no por el objeto: dos vueltas del poll pueden
    // traer el mismo documento listo, y remontar la confirmación por un objeto nuevo reiniciaría
    // el reloj sin que haya pasado nada nuevo.
    const freshId = fresh ? fresh.id : null;
    useEffect(() => {
        if (freshId === null || freshId === undefined) {
            setShowingFresh(false);
            return undefined;
        }
        setShowingFresh(true);
        const timer = setTimeout(() => setShowingFresh(false), FRESH_MS);
        return () => clearTimeout(timer);
    }, [freshId]);

    const freshText = () => {
        const title = (fresh && fresh.title) || '';
        const facts = fresh ? fresh.facts : null;
        const values = {title, n: facts, interpolation: {escapeValue: false}};
        if (typeof facts !== 'number') return t('ia_booster.feed.source_fresh_nofacts', values);
        return facts === 1
            ? t('ia_booster.feed.source_fresh_one', values)
            : t('ia_booster.feed.source_fresh_many', values);
    };

    const text = () => {
        if (showingFresh && fresh) return freshText();
        if (loading || !known) return t('ia_booster.feed.source_loading');
        const docs = unitLabel(t, documentCount,
            'ia_booster.feed.source_doc_one', 'ia_booster.feed.source_doc_many');
        const notes = unitLabel(t, noteCount,
            'ia_booster.feed.source_note_one', 'ia_booster.feed.source_note_many');
        // Se interpolan los HUECOS, no los textos: la frase se parte después para poder resaltar los
        // dos conteos, que es lo que hace el mockup.
        const line = t('ia_booster.feed.source_line', {
            docs: DOCS_SLOT,
            notes: NOTES_SLOT,
            interpolation: {escapeValue: false},
        });
        return withBoldCounts(line, docs, notes);
    };

    const cta = (
        <>
            {t('ia_booster.feed.source_cta')}
            <ArrowRight size={13} aria-hidden="true"/>
        </>
    );

    return (
        <div className={`iab-srcline ${showingFresh && fresh ? '-fresh' : ''}`} role="status">
            <span className="iab-sl-ic" aria-hidden="true">
                <FileText size={18}/>
                <span className="iab-sl-particles">
                    <i/><i/><i/><i/>
                </span>
            </span>
            <div className="iab-sl-body">
                <div className="iab-sl-txt">{text()}</div>
                <div className="iab-sl-sub">{t('ia_booster.feed.source_sub')}</div>
            </div>
            {drawer.available ? (
                <button type="button" className="iab-sl-link" onClick={() => drawer.openSources()}>
                    {cta}
                </button>
            ) : (
                <Link className="iab-sl-link" to={boosterFlowPath(clientId, SOURCES_ANCHOR)}>
                    {cta}
                </Link>
            )}
        </div>
    );
}

export default SourceLine;
